'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import { FeatherCalendar, FeatherChevronDown, FeatherChevronLeft, FeatherChevronRight } from '@subframe/core';
import { isSameDay, isSameMonth, startOfMonth } from 'date-fns';
import type { PeriodoCampoOpcao, PeriodoPresetKey, PeriodoValor } from '@/shared/types/filtroPeriodo';
import { cn } from '@/shared/utils';
import { brParaIso, deIsoData, deslocarMes, gradeDoMes, intervaloDoPreset, isoParaBr, maskDateBr, paraIsoData, periodoEstaAtivo, PERIODO_DIAS_SEMANA_PT, PERIODO_PRESETS, rotuloDoIntervalo, rotuloDoMes, rotuloDoPreset } from '@/shared/utils/periodoFiltro';
interface FiltroPeriodoProps {
  /** Datas que a lista sabe filtrar (ex.: criação, envio, visualização). A primeira é o padrão. */
  campos: readonly PeriodoCampoOpcao[];
  valor: PeriodoValor;
  onChange: (valor: PeriodoValor) => void;
  /** Frase do rodapé quando já há data escolhida — o mock mostra a contagem de resultados. */
  resumo?: string;
  /** Rascunho aberto no popover, para o pai calcular a prévia antes de aplicar. */
  onRascunhoChange?: (valor: PeriodoValor | null) => void;
  className?: string;
}
const RODAPE_SEM_DATA = 'Escolha um atalho ou selecione as datas no calendário.';
export function FiltroPeriodo(t0) {
  const $ = _c(187);
  const {
    campos,
    valor,
    onChange,
    resumo,
    onRascunhoChange,
    className
  } = t0;
  const [aberto, setAberto] = useState(false);
  const [campoRascunho, setCampoRascunho] = useState(valor.campo);
  const [inicioRascunho, setInicioRascunho] = useState(valor.inicio);
  const [fimRascunho, setFimRascunho] = useState(valor.fim);
  const [presetRascunho, setPresetRascunho] = useState(valor.preset);
  let t1;
  if ($[0] !== valor.inicio) {
    t1 = () => isoParaBr(valor.inicio);
    $[0] = valor.inicio;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [inicioTexto, setInicioTexto] = useState(t1);
  let t2;
  if ($[2] !== valor.fim) {
    t2 = () => isoParaBr(valor.fim);
    $[2] = valor.fim;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [fimTexto, setFimTexto] = useState(t2);
  let t3;
  if ($[4] !== valor.inicio) {
    t3 = () => startOfMonth(deIsoData(valor.inicio) ?? new Date());
    $[4] = valor.inicio;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [mesVisivel, setMesVisivel] = useState(t3);
  let t4;
  let t5;
  if ($[6] !== aberto || $[7] !== campoRascunho || $[8] !== fimRascunho || $[9] !== inicioRascunho || $[10] !== onRascunhoChange || $[11] !== presetRascunho) {
    t4 = () => {
      onRascunhoChange?.(aberto ? {
        campo: campoRascunho,
        inicio: inicioRascunho,
        fim: fimRascunho,
        preset: presetRascunho
      } : null);
    };
    t5 = [aberto, campoRascunho, fimRascunho, inicioRascunho, onRascunhoChange, presetRascunho];
    $[6] = aberto;
    $[7] = campoRascunho;
    $[8] = fimRascunho;
    $[9] = inicioRascunho;
    $[10] = onRascunhoChange;
    $[11] = presetRascunho;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== campos) {
    t6 = key => campos.find(campo => campo.key === key) ?? campos[0];
    $[14] = campos;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const campoDe = t6;
  let t7;
  if ($[16] !== valor) {
    t7 = periodoEstaAtivo(valor);
    $[16] = valor;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const ativo = t7;
  let t8;
  if ($[18] !== ativo || $[19] !== campoDe || $[20] !== valor) {
    t8 = ativo ? `${campoDe(valor.campo).label}: ${rotuloDoPreset(valor.preset) ?? rotuloDoIntervalo(valor)}` : "Per\xEDodo";
    $[18] = ativo;
    $[19] = campoDe;
    $[20] = valor;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const rotuloGatilho = t8;
  const rascunhoAtivo = Boolean(inicioRascunho);
  const textoInvalido = _temp;
  const temTextoInvalido = textoInvalido(inicioTexto) || textoInvalido(fimTexto);
  const podeAplicar = (rascunhoAtivo || ativo) && !temTextoInvalido;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = paraIsoData(new Date());
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const hoje = t9;
  let T0;
  let T1;
  let T2;
  let aplicar;
  let digitarFim;
  let digitarInicio;
  let limpar;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[23] !== aberto || $[24] !== ativo || $[25] !== campoRascunho || $[26] !== campos || $[27] !== className || $[28] !== fimRascunho || $[29] !== inicioRascunho || $[30] !== mesVisivel || $[31] !== onChange || $[32] !== podeAplicar || $[33] !== presetRascunho || $[34] !== rotuloGatilho || $[35] !== valor.campo || $[36] !== valor.fim || $[37] !== valor.inicio || $[38] !== valor.preset) {
    const dias = gradeDoMes(mesVisivel);
    const inicioRascunhoDate = deIsoData(inicioRascunho);
    const fimRascunhoDate = deIsoData(fimRascunho);
    let t24;
    if ($[60] !== valor.campo || $[61] !== valor.fim || $[62] !== valor.inicio || $[63] !== valor.preset) {
      const semear = () => {
        setCampoRascunho(valor.campo);
        setInicioRascunho(valor.inicio);
        setFimRascunho(valor.fim);
        setPresetRascunho(valor.preset);
        setInicioTexto(isoParaBr(valor.inicio));
        setFimTexto(isoParaBr(valor.fim));
        setMesVisivel(startOfMonth(deIsoData(valor.inicio) ?? new Date()));
      };
      t24 = proximo => {
        if (proximo) {
          semear();
        }
        setAberto(proximo);
      };
      $[60] = valor.campo;
      $[61] = valor.fim;
      $[62] = valor.inicio;
      $[63] = valor.preset;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    const alternarAbertura = t24;
    let t25;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = preset => {
        if (preset === "custom") {
          setPresetRascunho("custom");
          return;
        }
        const intervalo = intervaloDoPreset(preset);
        const inicioDate = deIsoData(intervalo.inicio);
        setPresetRascunho(preset);
        setInicioRascunho(intervalo.inicio);
        setFimRascunho(intervalo.fim);
        setInicioTexto(isoParaBr(intervalo.inicio));
        setFimTexto(isoParaBr(intervalo.fim));
        if (inicioDate) {
          setMesVisivel(startOfMonth(inicioDate));
        }
      };
      $[65] = t25;
    } else {
      t25 = $[65];
    }
    const selecionarPreset = t25;
    let t26;
    if ($[66] !== fimRascunho || $[67] !== inicioRascunho) {
      t26 = iso => {
        setPresetRascunho("custom");
        if (!inicioRascunho || fimRascunho) {
          setInicioRascunho(iso);
          setFimRascunho(null);
          setInicioTexto(isoParaBr(iso));
          setFimTexto("");
          return;
        }
        if (iso < inicioRascunho) {
          setFimRascunho(inicioRascunho);
          setFimTexto(isoParaBr(inicioRascunho));
          setInicioRascunho(iso);
          setInicioTexto(isoParaBr(iso));
          return;
        }
        setFimRascunho(iso);
        setFimTexto(isoParaBr(iso));
      };
      $[66] = fimRascunho;
      $[67] = inicioRascunho;
      $[68] = t26;
    } else {
      t26 = $[68];
    }
    const selecionarDia = t26;
    let t27;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = texto_0 => {
        setInicioTexto(texto_0);
        const iso_0 = brParaIso(texto_0);
        const date = deIsoData(iso_0);
        if (!iso_0 || !date) {
          setInicioRascunho(null);
          return;
        }
        setInicioRascunho(iso_0);
        setPresetRascunho("custom");
        setMesVisivel(startOfMonth(date));
      };
      $[69] = t27;
    } else {
      t27 = $[69];
    }
    digitarInicio = t27;
    let t28;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = texto_1 => {
        setFimTexto(texto_1);
        const iso_1 = brParaIso(texto_1);
        if (!iso_1) {
          setFimRascunho(null);
          return;
        }
        setFimRascunho(iso_1);
        setPresetRascunho("custom");
      };
      $[70] = t28;
    } else {
      t28 = $[70];
    }
    digitarFim = t28;
    let t29;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = () => {
        setInicioRascunho(null);
        setFimRascunho(null);
        setPresetRascunho(null);
        setInicioTexto("");
        setFimTexto("");
      };
      $[71] = t29;
    } else {
      t29 = $[71];
    }
    limpar = t29;
    let t30;
    if ($[72] !== campoRascunho || $[73] !== fimRascunho || $[74] !== inicioRascunho || $[75] !== onChange || $[76] !== podeAplicar || $[77] !== presetRascunho) {
      t30 = () => {
        if (!podeAplicar) {
          return;
        }
        onChange(inicioRascunho ? {
          campo: campoRascunho,
          inicio: inicioRascunho,
          fim: fimRascunho ?? inicioRascunho,
          preset: presetRascunho
        } : {
          campo: campoRascunho,
          inicio: null,
          fim: null,
          preset: null
        });
        setAberto(false);
      };
      $[72] = campoRascunho;
      $[73] = fimRascunho;
      $[74] = inicioRascunho;
      $[75] = onChange;
      $[76] = podeAplicar;
      $[77] = presetRascunho;
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    aplicar = t30;
    T2 = RadixPopover.Root;
    t21 = aberto;
    t22 = alternarAbertura;
    const t31 = ativo ? "border-brand-600 bg-brand-600/6 text-brand-600 hover:bg-brand-600/12" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50";
    let t32;
    if ($[79] !== className || $[80] !== t31) {
      t32 = cn("text-body-bold font-body-bold flex h-10 min-w-fit shrink-0 items-center gap-2 rounded-[8px] border border-solid px-3 transition-all duration-200", t31, className);
      $[79] = className;
      $[80] = t31;
      $[81] = t32;
    } else {
      t32 = $[81];
    }
    let t33;
    if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <FeatherCalendar className="h-4 w-4 shrink-0" />;
      $[82] = t33;
    } else {
      t33 = $[82];
    }
    let t34;
    if ($[83] !== rotuloGatilho) {
      t34 = <span className="whitespace-nowrap">{rotuloGatilho}</span>;
      $[83] = rotuloGatilho;
      $[84] = t34;
    } else {
      t34 = $[84];
    }
    let t35;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <FeatherChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />;
      $[85] = t35;
    } else {
      t35 = $[85];
    }
    if ($[86] !== t32 || $[87] !== t34) {
      t23 = <RadixPopover.Trigger asChild={true}><button type="button" className={t32}>{t33}{t34}{t35}</button></RadixPopover.Trigger>;
      $[86] = t32;
      $[87] = t34;
      $[88] = t23;
    } else {
      t23 = $[88];
    }
    T1 = RadixPopover.Portal;
    T0 = RadixPopover.Content;
    t17 = "start";
    t18 = 8;
    t19 = "z-[10010] flex w-[640px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-white shadow-lg";
    let t36;
    if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <span className="text-caption-bold font-caption-bold text-subtext-color mr-1">Filtrar por</span>;
      $[89] = t36;
    } else {
      t36 = $[89];
    }
    let t37;
    if ($[90] !== campoRascunho || $[91] !== campos) {
      let t38;
      if ($[93] !== campoRascunho) {
        t38 = campo_0 => <button key={campo_0.key} type="button" onClick={() => setCampoRascunho(campo_0.key)} className={cn("h-7 rounded-md px-3 text-[13px] leading-5 font-medium transition-colors duration-150", campoRascunho === campo_0.key ? "bg-brand-600 text-white" : "text-subtext-color bg-transparent")}>{campo_0.label}</button>;
        $[93] = campoRascunho;
        $[94] = t38;
      } else {
        t38 = $[94];
      }
      t37 = campos.map(t38);
      $[90] = campoRascunho;
      $[91] = campos;
      $[92] = t37;
    } else {
      t37 = $[92];
    }
    if ($[95] !== t37) {
      t20 = <div className="flex flex-wrap items-center gap-2 border-b border-solid border-neutral-100 bg-neutral-50 px-5 py-3.5">{t36}<div className="flex items-center gap-1 rounded-[8px] border border-solid border-neutral-200 bg-white p-[3px]">{t37}</div></div>;
      $[95] = t37;
      $[96] = t20;
    } else {
      t20 = $[96];
    }
    t15 = "flex items-stretch";
    let t38;
    if ($[97] !== presetRascunho) {
      t38 = PERIODO_PRESETS.map(preset_0 => <button key={preset_0.key} type="button" onClick={() => selecionarPreset(preset_0.key)} className={cn("text-body font-body flex h-[34px] items-center rounded-[8px] px-3 text-left transition-colors duration-150", presetRascunho === preset_0.key ? "bg-brand-600/10 text-brand-600 font-semibold" : "bg-transparent text-neutral-700")}>{preset_0.label}</button>);
      $[97] = presetRascunho;
      $[98] = t38;
    } else {
      t38 = $[98];
    }
    if ($[99] !== t38) {
      t16 = <div className="flex w-[188px] flex-none flex-col gap-0.5 border-r border-solid border-neutral-100 p-3">{t38}</div>;
      $[99] = t38;
      $[100] = t16;
    } else {
      t16 = $[100];
    }
    t12 = "flex min-w-0 flex-1 flex-col gap-3 px-5 py-4";
    let t39;
    if ($[101] !== mesVisivel) {
      t39 = () => setMesVisivel(deslocarMes(mesVisivel, -1));
      $[101] = mesVisivel;
      $[102] = t39;
    } else {
      t39 = $[102];
    }
    let t40;
    if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <FeatherChevronLeft className="h-4 w-4" />;
      $[103] = t40;
    } else {
      t40 = $[103];
    }
    let t41;
    if ($[104] !== t39) {
      t41 = <button type="button" aria-label={"M\xEAs anterior"} onClick={t39} className="text-subtext-color flex h-7 w-7 items-center justify-center rounded-[8px] hover:bg-neutral-100 hover:text-neutral-800">{t40}</button>;
      $[104] = t39;
      $[105] = t41;
    } else {
      t41 = $[105];
    }
    let t42;
    if ($[106] !== mesVisivel) {
      t42 = rotuloDoMes(mesVisivel);
      $[106] = mesVisivel;
      $[107] = t42;
    } else {
      t42 = $[107];
    }
    let t43;
    if ($[108] !== t42) {
      t43 = <span className="text-body-bold font-body-bold text-default-font">{t42}</span>;
      $[108] = t42;
      $[109] = t43;
    } else {
      t43 = $[109];
    }
    let t44;
    if ($[110] !== mesVisivel) {
      t44 = () => setMesVisivel(deslocarMes(mesVisivel, 1));
      $[110] = mesVisivel;
      $[111] = t44;
    } else {
      t44 = $[111];
    }
    let t45;
    if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
      t45 = <FeatherChevronRight className="h-4 w-4" />;
      $[112] = t45;
    } else {
      t45 = $[112];
    }
    let t46;
    if ($[113] !== t44) {
      t46 = <button type="button" aria-label={"Pr\xF3ximo m\xEAs"} onClick={t44} className="text-subtext-color flex h-7 w-7 items-center justify-center rounded-[8px] hover:bg-neutral-100 hover:text-neutral-800">{t45}</button>;
      $[113] = t44;
      $[114] = t46;
    } else {
      t46 = $[114];
    }
    if ($[115] !== t41 || $[116] !== t43 || $[117] !== t46) {
      t13 = <div className="flex items-center justify-between">{t41}{t43}{t46}</div>;
      $[115] = t41;
      $[116] = t43;
      $[117] = t46;
      $[118] = t13;
    } else {
      t13 = $[118];
    }
    if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="grid grid-cols-7">{PERIODO_DIAS_SEMANA_PT.map(_temp2)}</div>;
      $[119] = t14;
    } else {
      t14 = $[119];
    }
    t10 = "grid grid-cols-7 gap-y-0.5";
    t11 = dias.map(dia_0 => {
      const iso_2 = paraIsoData(dia_0);
      const extremo = Boolean(inicioRascunhoDate && isSameDay(dia_0, inicioRascunhoDate) || fimRascunhoDate && isSameDay(dia_0, fimRascunhoDate));
      const intermediario = !extremo && Boolean(inicioRascunho) && Boolean(fimRascunho) && iso_2 > String(inicioRascunho) && iso_2 < String(fimRascunho);
      const noMes = isSameMonth(dia_0, mesVisivel);
      return <button key={iso_2} type="button" onClick={() => selecionarDia(iso_2)} className={cn("flex h-[34px] items-center justify-center text-[13px] leading-5 tabular-nums transition-colors duration-150", extremo && "bg-brand-600 hover:bg-brand-700 rounded-[8px] font-semibold text-white", intermediario && "bg-brand-600/8 text-brand-600 rounded-none", !extremo && !intermediario && ["rounded-[8px] bg-transparent hover:bg-neutral-100", noMes ? "text-default-font" : "text-neutral-300", iso_2 === hoje && "font-semibold shadow-[inset_0_0_0_1px_var(--color-brand-600)]"])}>{dia_0.getDate()}</button>;
    });
    $[23] = aberto;
    $[24] = ativo;
    $[25] = campoRascunho;
    $[26] = campos;
    $[27] = className;
    $[28] = fimRascunho;
    $[29] = inicioRascunho;
    $[30] = mesVisivel;
    $[31] = onChange;
    $[32] = podeAplicar;
    $[33] = presetRascunho;
    $[34] = rotuloGatilho;
    $[35] = valor.campo;
    $[36] = valor.fim;
    $[37] = valor.inicio;
    $[38] = valor.preset;
    $[39] = T0;
    $[40] = T1;
    $[41] = T2;
    $[42] = aplicar;
    $[43] = digitarFim;
    $[44] = digitarInicio;
    $[45] = limpar;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
    $[59] = t23;
  } else {
    T0 = $[39];
    T1 = $[40];
    T2 = $[41];
    aplicar = $[42];
    digitarFim = $[43];
    digitarInicio = $[44];
    limpar = $[45];
    t10 = $[46];
    t11 = $[47];
    t12 = $[48];
    t13 = $[49];
    t14 = $[50];
    t15 = $[51];
    t16 = $[52];
    t17 = $[53];
    t18 = $[54];
    t19 = $[55];
    t20 = $[56];
    t21 = $[57];
    t22 = $[58];
    t23 = $[59];
  }
  let t24;
  if ($[120] !== t10 || $[121] !== t11) {
    t24 = <div className={t10}>{t11}</div>;
    $[120] = t10;
    $[121] = t11;
    $[122] = t24;
  } else {
    t24 = $[122];
  }
  let t25;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="text-caption-bold font-caption-bold text-subtext-color">De</span>;
    $[123] = t25;
  } else {
    t25 = $[123];
  }
  let t26;
  if ($[124] !== digitarInicio) {
    t26 = event => digitarInicio(maskDateBr(event.target.value));
    $[124] = digitarInicio;
    $[125] = t26;
  } else {
    t26 = $[125];
  }
  let t27;
  if ($[126] !== inicioTexto || $[127] !== t26) {
    t27 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t25}<input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={inicioTexto} onChange={t26} className="text-body font-body text-default-font focus:border-brand-600 focus:ring-brand-600/10 h-9 w-full rounded-[8px] border border-solid border-neutral-300 px-3 outline-none focus:ring-4" /></div>;
    $[126] = inicioTexto;
    $[127] = t26;
    $[128] = t27;
  } else {
    t27 = $[128];
  }
  let t28;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="text-body font-body pb-[9px] text-neutral-400">—</span>;
    $[129] = t28;
  } else {
    t28 = $[129];
  }
  let t29;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="text-caption-bold font-caption-bold text-subtext-color">Até</span>;
    $[130] = t29;
  } else {
    t29 = $[130];
  }
  let t30;
  if ($[131] !== digitarFim) {
    t30 = event_0 => digitarFim(maskDateBr(event_0.target.value));
    $[131] = digitarFim;
    $[132] = t30;
  } else {
    t30 = $[132];
  }
  let t31;
  if ($[133] !== fimTexto || $[134] !== t30) {
    t31 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t29}<input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={fimTexto} onChange={t30} className="text-body font-body text-default-font focus:border-brand-600 focus:ring-brand-600/10 h-9 w-full rounded-[8px] border border-solid border-neutral-300 px-3 outline-none focus:ring-4" /></div>;
    $[133] = fimTexto;
    $[134] = t30;
    $[135] = t31;
  } else {
    t31 = $[135];
  }
  let t32;
  if ($[136] !== t27 || $[137] !== t31) {
    t32 = <div className="flex items-end gap-2 border-t border-solid border-neutral-100 pt-3">{t27}{t28}{t31}</div>;
    $[136] = t27;
    $[137] = t31;
    $[138] = t32;
  } else {
    t32 = $[138];
  }
  let t33;
  if ($[139] !== t12 || $[140] !== t13 || $[141] !== t14 || $[142] !== t24 || $[143] !== t32) {
    t33 = <div className={t12}>{t13}{t14}{t24}{t32}</div>;
    $[139] = t12;
    $[140] = t13;
    $[141] = t14;
    $[142] = t24;
    $[143] = t32;
    $[144] = t33;
  } else {
    t33 = $[144];
  }
  let t34;
  if ($[145] !== t15 || $[146] !== t16 || $[147] !== t33) {
    t34 = <div className={t15}>{t16}{t33}</div>;
    $[145] = t15;
    $[146] = t16;
    $[147] = t33;
    $[148] = t34;
  } else {
    t34 = $[148];
  }
  let t35;
  if ($[149] !== fimRascunho || $[150] !== inicioRascunho || $[151] !== rascunhoAtivo || $[152] !== resumo) {
    t35 = rascunhoAtivo ? resumo ?? rotuloDoIntervalo({
      inicio: inicioRascunho,
      fim: fimRascunho
    }) : RODAPE_SEM_DATA;
    $[149] = fimRascunho;
    $[150] = inicioRascunho;
    $[151] = rascunhoAtivo;
    $[152] = resumo;
    $[153] = t35;
  } else {
    t35 = $[153];
  }
  let t36;
  if ($[154] !== t35) {
    t36 = <span className="text-subtext-color text-[13px] leading-5">{t35}</span>;
    $[154] = t35;
    $[155] = t36;
  } else {
    t36 = $[155];
  }
  let t37;
  if ($[156] !== limpar) {
    t37 = <button type="button" onClick={limpar} className="text-body-bold font-body-bold text-subtext-color h-9 rounded-[8px] px-3.5 hover:bg-neutral-100 hover:text-neutral-800">Limpar</button>;
    $[156] = limpar;
    $[157] = t37;
  } else {
    t37 = $[157];
  }
  const t38 = !podeAplicar;
  const t39 = podeAplicar ? "hover:bg-brand-600" : "cursor-not-allowed opacity-80 saturate-[0.7]";
  let t40;
  if ($[158] !== t39) {
    t40 = cn("text-body-bold font-body-bold bg-brand-700 h-9 rounded-[8px] px-4 text-white", t39);
    $[158] = t39;
    $[159] = t40;
  } else {
    t40 = $[159];
  }
  let t41;
  if ($[160] !== aplicar || $[161] !== t38 || $[162] !== t40) {
    t41 = <button type="button" onClick={aplicar} disabled={t38} className={t40}>Aplicar período</button>;
    $[160] = aplicar;
    $[161] = t38;
    $[162] = t40;
    $[163] = t41;
  } else {
    t41 = $[163];
  }
  let t42;
  if ($[164] !== t37 || $[165] !== t41) {
    t42 = <div className="flex items-center gap-2">{t37}{t41}</div>;
    $[164] = t37;
    $[165] = t41;
    $[166] = t42;
  } else {
    t42 = $[166];
  }
  let t43;
  if ($[167] !== t36 || $[168] !== t42) {
    t43 = <div className="flex items-center justify-between gap-4 border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3">{t36}{t42}</div>;
    $[167] = t36;
    $[168] = t42;
    $[169] = t43;
  } else {
    t43 = $[169];
  }
  let t44;
  if ($[170] !== T0 || $[171] !== t17 || $[172] !== t18 || $[173] !== t19 || $[174] !== t20 || $[175] !== t34 || $[176] !== t43) {
    t44 = <T0 align={t17} sideOffset={t18} className={t19}>{t20}{t34}{t43}</T0>;
    $[170] = T0;
    $[171] = t17;
    $[172] = t18;
    $[173] = t19;
    $[174] = t20;
    $[175] = t34;
    $[176] = t43;
    $[177] = t44;
  } else {
    t44 = $[177];
  }
  let t45;
  if ($[178] !== T1 || $[179] !== t44) {
    t45 = <T1>{t44}</T1>;
    $[178] = T1;
    $[179] = t44;
    $[180] = t45;
  } else {
    t45 = $[180];
  }
  let t46;
  if ($[181] !== T2 || $[182] !== t21 || $[183] !== t22 || $[184] !== t23 || $[185] !== t45) {
    t46 = <T2 open={t21} onOpenChange={t22}>{t23}{t45}</T2>;
    $[181] = T2;
    $[182] = t21;
    $[183] = t22;
    $[184] = t23;
    $[185] = t45;
    $[186] = t46;
  } else {
    t46 = $[186];
  }
  return t46;
}
function _temp2(dia) {
  return <span key={dia} className="text-caption-bold font-caption-bold flex h-6 items-center justify-center text-neutral-400">{dia}</span>;
}
function _temp(texto) {
  return texto.trim() !== "" && !brParaIso(texto);
}